import React from "react";
import { Sparkles, Layers, ShieldCheck, MapPin, Building2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { resolveImageUrl } from "@/utils/api";

export default function AboutUs() {
  const { settings } = useSiteSettings();
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">
      
      {/* Brand Hero */}
      <div className="bg-[#1C1917] text-white rounded-3xl border-2 border-[#D4AF37] p-8 sm:p-14 relative overflow-hidden shadow-2xl">
        <div className="relative z-10 max-w-2xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/20 text-[#E6C687] text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            Pakistan's Premium Lace Brand
          </div>
          <h1 
            data-testid="about-page-title"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight"
          >
            The Heritage & Craft of Anwer Fancy Lace
          </h1>
          <p className="text-stone-300 text-xs sm:text-sm leading-relaxed">
            Specializing in original China-imported luxury laces, bridal borders, diamond sitara trims, and high-fashion embellishments since our establishment.
          </p>
        </div>
      </div>

      {/* Story & Commitment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-5 text-xs sm:text-sm text-stone-700 leading-relaxed">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#996515]">
            Our Mission & Origin
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">
            Bringing High-Precision China Lace Artistry to Pakistani Couture
          </h2>
          <p>
            Anwer Fancy Lace was established with a singular vision: to eliminate the hassle of lace mismatching and low-grade domestic trims by supplying original, factory-finished China fancy laces in pre-calculated complete dress sets.
          </p>
          <p>
            Whether you are an established bridal couture designer in Lahore or Karachi, an embroidery house in Faisalabad, or an individual crafting a bespoke festive wardrobe, we ensure consistent diamond sitara sparkle, authentic zari thread strength, and flawless color matching.
          </p>

          <div className="space-y-2 pt-2 text-xs">
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <span>Direct factory import from premium lace manufacturing hubs in China</span>
            </div>
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <span>Pre-packed Complete Sets with defined Colors, Taan and Gaz for every design</span>
            </div>
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-[#D4AF37] shrink-0" />
              <span>Full wholesale and retail fulfillment with nationwide cash on delivery</span>
            </div>
          </div>
        </div>

        <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border-2 border-[#D4AF37]/40 shadow-2xl bg-[#FAF8F5]">
          {settings.about_us_image ? (
            <img
              src={resolveImageUrl(settings.about_us_image)}
              alt="About Anwer Fancy Lace"
              data-testid="about-page-image"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-stone-400 gap-2 p-6 text-center">
              <Layers className="w-12 h-12 text-[#D4AF37]/60" />
              <p className="text-xs italic">
                Admin can upload the About Us image in Settings.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Core Specialties */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-3">
          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/15 text-[#996515] flex items-center justify-center font-bold">
            <Sparkles className="w-5 h-5" />
          </div>
          <h3 className="font-heading font-bold text-base text-stone-900">Original China Diamond Sitara</h3>
          <p className="text-xs text-stone-600 leading-relaxed">
            Faceted micro-sequins with mirror polish reflection that enhance luxury wedding formals under festive lights.
          </p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-3">
          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/15 text-[#996515] flex items-center justify-center font-bold">
            <Layers className="w-5 h-5" />
          </div>
          <h3 className="font-heading font-bold text-base text-stone-900">Complete Set Packaging</h3>
          <p className="text-xs text-stone-600 leading-relaxed">
            Carefully portioned yardage so tailor masters have the exact borders required for front, back, sleeves, and dupatta.
          </p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-3">
          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/15 text-[#996515] flex items-center justify-center font-bold">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <h3 className="font-heading font-bold text-base text-stone-900">Wholesale Partnership</h3>
          <p className="text-xs text-stone-600 leading-relaxed">
            Trusted wholesale partner to cloth merchants and bridal boutiques nationwide with fast inter-city cargo service.
          </p>
        </div>
      </div>

    </div>
  );
}
